import { ActionButton, ActionButtonType, StatusBadge } from "@octopusdeploy/design-system-components";
import type { MachineModelHealthStatus } from "@octopusdeploy/octopus-server-client";
import * as React from "react";
import { DisabledMachineIcon } from "~/areas/infrastructure/components/MachineHealthStatusIcons/DisabledMachineIcon";
import { MachineHealthStatusIcon } from "~/areas/infrastructure/components/MachineHealthStatusIcons/MachineHealthStatusIcon";
import { machineHealthStatusToStatusBadgeStatus } from "~/areas/infrastructure/components/MachineHealthStatusIcons/machineHealthStatusToStatusBadgeStatus";
import { SmallCloseButton } from "~/areas/infrastructure/components/MachinesLayout/BaseAllMachinesSummary";
import { useOctopusFeatureToggle } from "~/hooks/useOctopusFeatureToggle";
import MachineHealthStatusHelper from "~/utils/MachineHealthStatusHelper";
import styles from "./styles.module.less";
export interface MachineHealthSummaryLinkProps {
    count?: number;
    healthStatus: MachineModelHealthStatus;
    getLinkText: (status: MachineModelHealthStatus, count: number, friendlyName: string) => string;
    allowSelection: boolean;
    isSelected: boolean;
    onSelect: (e: React.MouseEvent, healthStatus: MachineModelHealthStatus) => void;
    onClearSelect?: (e: React.MouseEvent) => void;
}
export function MachineHealthSummaryLink({ count, healthStatus, getLinkText, allowSelection, isSelected, onSelect, onClearSelect }: MachineHealthSummaryLinkProps) {
    const isNewStatusIconsFeatureEnabled = useOctopusFeatureToggle("new-status-icons", false);
    if (!count || count === 0) {
        // Don't show links if there's nothing to report.
        return null;
    }
    const text = getLinkText(healthStatus, count, MachineHealthStatusHelper.getFriendlyName(healthStatus).toLowerCase());
    const icon = isNewStatusIconsFeatureEnabled ? <StatusBadge status={machineHealthStatusToStatusBadgeStatus(healthStatus)} size="small"/> : <MachineHealthStatusIcon healthStatus={healthStatus}/>;
    // If filtering health statuses from the sidebar, just show the health statuses that they've chosen to filter (and don't show them as links).
    if (!allowSelection) {
        return <MachineHealthSummaryText icon={icon} text={text}/>;
    }
    return <SelectableMachineHealthSummaryButton healthStatus={healthStatus} linkText={text} isSelected={isSelected} onSelect={onSelect} onClearSelect={onClearSelect} isNewStatusIconsFeatureEnabled={isNewStatusIconsFeatureEnabled}/>;
}
export interface DisabledMachineHealthSummaryLinkProps {
    count?: number;
    allowSelection: boolean;
    isSelected: boolean;
    onSelect: (e: React.MouseEvent) => void;
    onClearSelect?: (e: React.MouseEvent) => void;
}
export function DisabledMachineHealthSummaryLink({ count, isSelected, onSelect, onClearSelect, allowSelection }: DisabledMachineHealthSummaryLinkProps) {
    const isNewStatusIconsFeatureEnabled = useOctopusFeatureToggle("new-status-icons", false);
    if (!count || count === 0) {
        // Don't show links if there's nothing to report.
        return null;
    }
    const text = `${count.toLocaleString()} disabled`;
    const icon = isNewStatusIconsFeatureEnabled ? <StatusBadge status="disabled" size="small"/> : <DisabledMachineIcon />;
    if (!allowSelection) {
        return <MachineHealthSummaryText icon={icon} text={text} onClearSelect={onClearSelect}/>;
    }
    return <DisabledSelectableMachineHealthSummaryButton linkText={text} isSelected={isSelected} onSelect={onSelect} onClearSelect={onClearSelect} isNewStatusIconsFeatureEnabled={isNewStatusIconsFeatureEnabled}/>;
}
interface MachineHealthSummaryTextProps {
    icon: React.ReactNode;
    text: string;
    onClearSelect?: (e: React.MouseEvent) => void;
}
function MachineHealthSummary({ children }: React.PropsWithChildren<{}>) {
    return <div className={styles.summary}>{children}</div>;
}
function MachineHealthSummaryText({ icon, text, onClearSelect }: MachineHealthSummaryTextProps) {
    return (<MachineHealthSummary>
            {icon}
            {text}
            {onClearSelect && <SmallCloseButton onClose={onClearSelect}/>}
        </MachineHealthSummary>);
}
interface MachineHealthSummaryButtonProps {
    healthStatus: MachineModelHealthStatus;
    label: string;
    isNewStatusIconsFeatureEnabled: boolean;
    onSelect: (e: React.MouseEvent, healthStatus: MachineModelHealthStatus) => void;
}
function MachineHealthSummaryButton({ healthStatus, onSelect, label, isNewStatusIconsFeatureEnabled }: MachineHealthSummaryButtonProps) {
    const handleSelect = React.useCallback((ev: React.MouseEvent) => {
        onSelect(ev, healthStatus);
    }, [onSelect, healthStatus]);
    const icon = isNewStatusIconsFeatureEnabled ? <StatusBadge status={machineHealthStatusToStatusBadgeStatus(healthStatus)} size="small"/> : <MachineHealthStatusIcon healthStatus={healthStatus}/>;
    return <ActionButton icon={icon} type={ActionButtonType.Ternary} label={label} onClick={handleSelect}/>;
}
interface SelectableMachineHealthSummaryButtonProps {
    healthStatus: MachineModelHealthStatus;
    linkText: string;
    isSelected: boolean;
    isNewStatusIconsFeatureEnabled: boolean;
    onClearSelect?: (e: React.MouseEvent) => void;
    onSelect: (e: React.MouseEvent, status: MachineModelHealthStatus) => void;
}
function SelectableMachineHealthSummaryButton({ healthStatus, isSelected, linkText, onClearSelect, onSelect, isNewStatusIconsFeatureEnabled }: SelectableMachineHealthSummaryButtonProps) {
    const icon = isNewStatusIconsFeatureEnabled ? <StatusBadge status={machineHealthStatusToStatusBadgeStatus(healthStatus)} size="small"/> : <MachineHealthStatusIcon healthStatus={healthStatus}/>;
    return isSelected ? (<MachineHealthSummaryText text={linkText} icon={icon} onClearSelect={onClearSelect}/>) : (<MachineHealthSummaryButton label={linkText} healthStatus={healthStatus} onSelect={onSelect} isNewStatusIconsFeatureEnabled={isNewStatusIconsFeatureEnabled}/>);
}
function DisabledMachineHealthSummaryButton({ label, onSelect, isNewStatusIconsFeatureEnabled }: {
    label: string;
    onSelect: React.MouseEventHandler;
    isNewStatusIconsFeatureEnabled: boolean;
}) {
    const icon = isNewStatusIconsFeatureEnabled ? <StatusBadge status="disabled" size="small"/> : <DisabledMachineIcon />;
    return <ActionButton icon={icon} type={ActionButtonType.Ternary} label={label} onClick={onSelect}/>;
}
interface DisabledSelectableMachineHealthSummaryButtonProps {
    linkText: string;
    isSelected: boolean;
    isNewStatusIconsFeatureEnabled: boolean;
    onClearSelect?: (e: React.MouseEvent) => void;
    onSelect: (e: React.MouseEvent) => void;
}
function DisabledSelectableMachineHealthSummaryButton({ isSelected, linkText, onClearSelect, onSelect, isNewStatusIconsFeatureEnabled }: DisabledSelectableMachineHealthSummaryButtonProps) {
    const icon = isNewStatusIconsFeatureEnabled ? <StatusBadge status="disabled" size="small"/> : <DisabledMachineIcon />;
    return isSelected ? (<MachineHealthSummaryText text={linkText} icon={icon} onClearSelect={onClearSelect}/>) : (<DisabledMachineHealthSummaryButton label={linkText} onSelect={onSelect} isNewStatusIconsFeatureEnabled={isNewStatusIconsFeatureEnabled}/>);
}
